import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';

export default function Dashboard() {
  const { user, loading: authLoading, signOut } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);
  const [stats, setStats] = useState({
    totalBoxes: 24,
    storedBoxes: 18,
    inTransitBoxes: 4,
    deliveredBoxes: 2,
    categories: 6,
    recentActivity: 12
  });

  // Giriş kontrolü
  useEffect(() => {
    if (!authLoading && !user) {
      navigate('/login');
    }
  }, [user, authLoading, navigate]);

  useEffect(() => {
    if (user) {
      checkAdminStatus();
      setLoading(false);
    }
  }, [user]);

  const checkAdminStatus = async () => {
    if (!user) return;
    
    try {
      const { data: profileData, error } = await supabase
        .from('profiles')
        .select('is_admin, email')
        .eq('id', user.id)
        .maybeSingle();

      if (error) {
        console.error('Profil yüklenirken hata:', error);
        return;
      }

      if (!profileData) {
        const isAdminEmail = user.email === 'fadiloglu@kalyonkentsel.com';
        
        const { data: newProfile, error: insertError } = await supabase
          .from('profiles')
          .insert({
            id: user.id,
            email: user.email,
            full_name: user.user_metadata?.full_name || user.email?.split('@')[0] || '',
            phone: user.user_metadata?.phone || '',
            is_admin: isAdminEmail,
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString(),
          })
          .select()
          .single();

        if (!insertError && newProfile) {
          setIsAdmin(isAdminEmail);
        }
      } else {
        setIsAdmin(profileData.is_admin === true);
      }
    } catch (err) {
      console.error('Admin kontrolü yapılamadı:', err);
    }
  };

  const handleLogout = async () => {
    await signOut();
    navigate('/');
  };

  if (authLoading || loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: '#121C2E' }}>
        <div className="text-center">
          <i className="ri-loader-4-line text-4xl text-orange-500 animate-spin"></i>
          <p className="mt-4 text-white/70">Yükleniyor...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return null;
  }

  return (
    <div className="min-h-screen" style={{ backgroundColor: '#121C2E' }}>
      {/* Floating Elements */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-20 left-10 w-32 h-32 bg-white/5 rounded-full blur-xl animate-pulse"></div>
        <div className="absolute top-40 right-20 w-24 h-24 bg-purple-400/10 rounded-full blur-xl animate-pulse delay-1000"></div>
        <div className="absolute bottom-40 left-1/4 w-40 h-40 bg-white/5 rounded-full blur-xl animate-pulse delay-2000"></div>
      </div>

      {/* Header */}
      <header className="relative z-10 bg-[#1a2942]/80 backdrop-blur-md border-b border-white/10 sticky top-0">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex justify-between items-center">
            <div className="flex items-center">
              <Link to="/" className="text-2xl font-bold text-white cursor-pointer">
                pad<span className="text-orange-500">baq</span>
              </Link>
            </div>
            <nav className="flex items-center space-x-4">
              <Link to="/dashboard" className="text-orange-500 font-medium px-4 py-2 rounded-lg bg-white/10 whitespace-nowrap cursor-pointer">Panel</Link>
              <Link to="/boxes" className="text-white/80 hover:text-white px-4 py-2 rounded-lg hover:bg-white/10 transition-all whitespace-nowrap cursor-pointer">Koli Yönetimi</Link>
              <Link to="/profile" className="text-white/80 hover:text-white px-4 py-2 rounded-lg hover:bg-white/10 transition-all whitespace-nowrap cursor-pointer">Profil</Link>
              {isAdmin && (
                <Link to="/admin" className="text-teal-400 hover:text-teal-300 px-4 py-2 rounded-lg hover:bg-white/10 transition-all whitespace-nowrap cursor-pointer">
                  <i className="ri-shield-star-line mr-2"></i>
                  Admin
                </Link>
              )}
              <button 
                onClick={handleLogout}
                className="text-white/80 hover:text-white px-4 py-2 rounded-lg hover:bg-white/10 transition-all whitespace-nowrap cursor-pointer"
              >
                <i className="ri-logout-circle-line mr-2"></i>
                Çıkış
              </button>
            </nav>
          </div>
        </div>
      </header>

      <main className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Welcome Section */}
        <div className="mb-8">
          <h2 className="text-3xl font-bold text-white mb-2">Hoş Geldiniz!</h2>
          <p className="text-white/70">Kolilerinizi dijital ortamda yönetin ve takip edin</p>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
          <div className="bg-white/10 backdrop-blur-sm rounded-2xl shadow-lg p-6 border border-white/10 hover:shadow-xl transition-all">
            <div className="flex items-center">
              <div className="w-12 h-12 bg-gradient-to-r from-purple-500 to-purple-600 rounded-xl flex items-center justify-center">
                <i className="ri-archive-line text-white text-xl"></i>
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-white/70">Toplam Koli</p>
                <p className="text-2xl font-bold text-white">{stats.totalBoxes}</p>
              </div>
            </div>
          </div>

          <div className="bg-white/10 backdrop-blur-sm rounded-2xl shadow-lg p-6 border border-white/10 hover:shadow-xl transition-all">
            <div className="flex items-center">
              <div className="w-12 h-12 bg-gradient-to-r from-green-500 to-green-600 rounded-xl flex items-center justify-center">
                <i className="ri-checkbox-circle-line text-white text-xl"></i>
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-white/70">Depolandı</p>
                <p className="text-2xl font-bold text-white">{stats.storedBoxes}</p>
              </div>
            </div>
          </div>

          <div className="bg-white/10 backdrop-blur-sm rounded-2xl shadow-lg p-6 border border-white/10 hover:shadow-xl transition-all">
            <div className="flex items-center">
              <div className="w-12 h-12 bg-gradient-to-r from-orange-500 to-orange-600 rounded-xl flex items-center justify-center">
                <i className="ri-truck-line text-white text-xl"></i>
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-white/70">Nakliyede</p>
                <p className="text-2xl font-bold text-white">{stats.inTransitBoxes}</p>
              </div>
            </div>
          </div>

          <div className="bg-white/10 backdrop-blur-sm rounded-2xl shadow-lg p-6 border border-white/10 hover:shadow-xl transition-all">
            <div className="flex items-center">
              <div className="w-12 h-12 bg-gradient-to-r from-cyan-500 to-cyan-600 rounded-xl flex items-center justify-center">
                <i className="ri-check-double-line text-white text-xl"></i>
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-white/70">Teslim Edildi</p>
                <p className="text-2xl font-bold text-white">{stats.deliveredBoxes}</p>
              </div>
            </div>
          </div>

          <div className="bg-white/10 backdrop-blur-sm rounded-2xl shadow-lg p-6 border border-white/10 hover:shadow-xl transition-all">
            <div className="flex items-center">
              <div className="w-12 h-12 bg-gradient-to-r from-pink-500 to-pink-600 rounded-xl flex items-center justify-center">
                <i className="ri-folder-line text-white text-xl"></i>
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-white/70">Kategori</p>
                <p className="text-2xl font-bold text-white">{stats.categories}</p>
              </div>
            </div>
          </div>

          <div className="bg-white/10 backdrop-blur-sm rounded-2xl shadow-lg p-6 border border-white/10 hover:shadow-xl transition-all">
            <div className="flex items-center">
              <div className="w-12 h-12 bg-gradient-to-r from-amber-500 to-amber-600 rounded-xl flex items-center justify-center">
                <i className="ri-time-line text-white text-xl"></i>
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-white/70">Son Aktivite</p>
                <p className="text-2xl font-bold text-white">{stats.recentActivity}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Quick Actions */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
          <div className="bg-white/10 backdrop-blur-sm rounded-2xl shadow-lg p-6 border border-white/10">
            <h3 className="text-lg font-semibold text-white mb-4">Hızlı İşlemler</h3>
            <div className="space-y-3">
              <Link 
                to="/boxes/new" 
                className="flex items-center p-3 bg-purple-500/20 rounded-lg hover:bg-purple-500/30 transition-colors cursor-pointer border border-purple-500/30"
              >
                <i className="ri-add-line text-purple-400 text-xl mr-3"></i>
                <span className="text-purple-300 font-medium whitespace-nowrap">Yeni Koli Ekle</span>
              </Link>
              <Link 
                to="/boxes" 
                className="flex items-center p-3 bg-orange-500/20 rounded-lg hover:bg-orange-500/30 transition-colors cursor-pointer border border-orange-500/30"
              >
                <i className="ri-qr-scan-2-line text-orange-400 text-xl mr-3"></i>
                <span className="text-orange-300 font-medium whitespace-nowrap">QR Kod Tara</span>
              </Link>
              <Link 
                to="/boxes" 
                className="flex items-center p-3 bg-green-500/20 rounded-lg hover:bg-green-500/30 transition-colors cursor-pointer border border-green-500/30"
              >
                <i className="ri-search-line text-green-400 text-xl mr-3"></i>
                <span className="text-green-300 font-medium whitespace-nowrap">Koli Ara</span>
              </Link>
            </div>
          </div>

          <div className="bg-white/10 backdrop-blur-sm rounded-2xl shadow-lg p-6 border border-white/10">
            <h3 className="text-lg font-semibold text-white mb-4">Son Aktiviteler</h3>
            <div className="space-y-3">
              <div className="flex items-center p-3 bg-white/5 rounded-lg border border-white/10">
                <i className="ri-archive-line text-green-400 text-xl mr-3"></i>
                <div>
                  <p className="text-sm font-medium text-white">Kış Kıyafetleri</p>
                  <p className="text-xs text-white/60">Depolandı - 2 saat önce</p>
                </div>
              </div>
              <div className="flex items-center p-3 bg-white/5 rounded-lg border border-white/10">
                <i className="ri-truck-line text-orange-400 text-xl mr-3"></i>
                <div>
                  <p className="text-sm font-medium text-white">Mutfak Eşyaları</p>
                  <p className="text-xs text-white/60">Nakliyede - 5 saat önce</p>
                </div>
              </div>
              <div className="flex items-center p-3 bg-white/5 rounded-lg border border-white/10">
                <i className="ri-edit-line text-purple-400 text-xl mr-3"></i>
                <div>
                  <p className="text-sm font-medium text-white">Elektronik Eşyalar</p>
                  <p className="text-xs text-white/60">Güncellendi - 1 gün önce</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Feature Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <div className="bg-gradient-to-br from-purple-500/20 to-purple-600/20 backdrop-blur-sm rounded-2xl p-6 border border-purple-500/30 hover:shadow-xl transition-all">
            <div className="w-12 h-12 bg-purple-500/30 rounded-xl flex items-center justify-center mb-4">
              <i className="ri-qr-code-line text-purple-300 text-2xl"></i>
            </div>
            <h3 className="text-lg font-semibold text-white mb-2">QR Kod Sistemi</h3>
            <p className="text-white/70 text-sm mb-4">Her koli için benzersiz QR kod oluşturun ve anında erişin</p>
            <Link to="/boxes" className="text-purple-300 hover:text-purple-200 text-sm font-medium cursor-pointer">
              Detaylı Bilgi <i className="ri-arrow-right-line ml-1"></i>
            </Link>
          </div>

          <div className="bg-gradient-to-br from-orange-500/20 to-orange-600/20 backdrop-blur-sm rounded-2xl p-6 border border-orange-500/30 hover:shadow-xl transition-all">
            <div className="w-12 h-12 bg-orange-500/30 rounded-xl flex items-center justify-center mb-4">
              <i className="ri-price-tag-3-line text-orange-300 text-2xl"></i>
            </div>
            <h3 className="text-lg font-semibold text-white mb-2">Etiket Yönetimi</h3>
            <p className="text-white/70 text-sm mb-4">Kolilerinizi kategorilere ayırın ve özel etiketler oluşturun</p>
            <Link to="/boxes" className="text-orange-300 hover:text-orange-200 text-sm font-medium cursor-pointer">
              Detaylı Bilgi <i className="ri-arrow-right-line ml-1"></i>
            </Link>
          </div>

          <div className="bg-gradient-to-br from-green-500/20 to-green-600/20 backdrop-blur-sm rounded-2xl p-6 border border-green-500/30 hover:shadow-xl transition-all">
            <div className="w-12 h-12 bg-green-500/30 rounded-xl flex items-center justify-center mb-4">
              <i className="ri-search-line text-green-300 text-2xl"></i>
            </div>
            <h3 className="text-lg font-semibold text-white mb-2">Akıllı Arama</h3>
            <p className="text-white/70 text-sm mb-4">Koli adı, etiket veya içerik ile arama yapın</p>
            <Link to="/boxes" className="text-green-300 hover:text-green-200 text-sm font-medium cursor-pointer">
              Detaylı Bilgi <i className="ri-arrow-right-line ml-1"></i>
            </Link>
          </div>
        </div>

        {/* CTA Banner */}
        <div className="bg-gradient-to-r from-orange-500/20 to-purple-500/20 backdrop-blur-sm rounded-2xl p-8 border border-white/20 text-center">
          <h3 className="text-2xl font-bold text-white mb-3">İlk Kolinizi Ekleyin!</h3>
          <p className="text-white/70 mb-6 max-w-2xl mx-auto">
            Kolilerinizi dijital ortamda organize etmeye başlayın. QR kod ile anında erişim sağlayın.
          </p>
          <Link
            to="/boxes/new"
            className="inline-flex items-center px-8 py-3 bg-gradient-to-r from-orange-500 to-orange-600 text-white rounded-xl hover:from-orange-600 hover:to-orange-700 transition-all shadow-lg hover:shadow-xl cursor-pointer whitespace-nowrap"
          >
            <i className="ri-add-line mr-2"></i>
            Yeni Koli Ekle
          </Link>
        </div>
      </main>

      {/* Chat Support Button */}
      <div className="fixed bottom-6 right-6 z-50">
        <button className="bg-gradient-to-r from-orange-500 to-orange-600 text-white w-14 h-14 rounded-full shadow-2xl hover:shadow-3xl transition-all duration-300 hover:scale-110 flex items-center justify-center cursor-pointer">
          <i className="ri-chat-3-line text-xl"></i>
        </button>
      </div>
    </div>
  );
}
